/**
 * Filing one — the catalog's create dialog, with a file picker on it.
 *
 * The order matters and is the only interesting thing here. Screenshots live at
 * `issues/{id}/{n}.png`, so the id has to exist before a byte can move, and the id comes from
 * a counter transaction. Making that two steps for the person filing ("create it, now go
 * upload") would lose screenshots, because the moment you want one is the moment you saw the
 * thing. So the picker **stages** files in local state and the dialog does the two writes in
 * order on submit: create, then upload against the id it got back.
 *
 * An upload that fails does not lose the issue. The record is already there; the dialog says
 * the screenshot did not make it and hands over the detail page, where it can be added again.
 *
 * Two error slots, because they are two different messages: a missing line is the **title
 * field's** problem and belongs under it, while a refused file or a write that did not go
 * through belongs to the dialog. Stacking both on one field would put "PNG, JPEG or WebP"
 * under a text input.
 */

import { Button, Dialog, Field, SelectField, TextAreaField } from '@atlas-nook/design-system';
import { useRef, useState } from 'react';
import type { JSX } from 'react';

import { createIssue, uploadIssueScreenshots } from '../data/writes.js';
import { ISSUE_AREAS, ISSUE_KINDS } from '../data/issues.js';
import type { IssueArea, IssueKind } from '../data/issues.js';
import { ADMIN_STRINGS as S } from '../strings.js';

/** The same three types the catalog illustration takes, at a screenshot's size. */
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024;

export function checkScreenshot(file: File): 'wrong-type' | 'too-large' | null {
  if (!TYPES.includes(file.type)) return 'wrong-type';
  if (file.size > MAX_BYTES) return 'too-large';
  return null;
}

export function NewIssueDialog({
  open,
  onClose,
  onFiled,
}: {
  open: boolean;
  onClose: () => void;
  onFiled: (id: string) => void;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [area, setArea] = useState<IssueArea>('studio');
  const [kind, setKind] = useState<IssueKind>('bug');
  const [step, setStep] = useState('');
  const [note, setNote] = useState('');
  const [staged, setStaged] = useState<File[]>([]);
  const [titleError, setTitleError] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  function stage(files: FileList | null): void {
    const picked = [...(files ?? [])];
    const refusal = picked.map(checkScreenshot).find((result) => result !== null);
    if (refusal !== undefined && refusal !== null) {
      setProblem(refusal === 'wrong-type' ? S.issues.shotsWrongType : S.issues.shotsTooLarge);
      return;
    }
    setProblem(null);
    setStaged((was) => [...was, ...picked]);
  }

  async function file(): Promise<void> {
    const line = title.trim();
    if (line === '') {
      setTitleError(S.issues.titleRequired);
      return;
    }

    setTitleError(undefined);
    setProblem(null);
    setWorking(true);
    try {
      const id = await createIssue({
        title: line,
        area,
        kind,
        ...(step.trim() === '' ? {} : { step: step.trim() }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      });

      if (staged.length > 0) {
        // The issue is filed either way — a screenshot that did not upload is a missing
        // picture, not a lost finding.
        try {
          await uploadIssueScreenshots(id, staged);
        } catch {
          setProblem(S.issues.shotsFailed);
        }
      }

      setTitle('');
      setStep('');
      setNote('');
      setStaged([]);
      onFiled(id);
    } catch {
      setProblem(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={S.issues.newTitle}
      actions={
        <>
          <Button tone="quiet" onClick={onClose}>
            {S.common.cancel}
          </Button>
          <Button tone="primary" working={working} onClick={() => void file()}>
            {S.issues.file}
          </Button>
        </>
      }
    >
      <p className="note">{S.issues.newBody}</p>
      <div className="stack">
        <Field
          label={S.issues.titleField}
          help={S.issues.titleHelp}
          value={title}
          {...(titleError === undefined ? {} : { error: titleError })}
          onChange={setTitle}
        />

        <div className="frow">
          <SelectField
            label={S.issues.areaField}
            value={area}
            onChange={(value) => setArea(value as IssueArea)}
          >
            {ISSUE_AREAS.map((value) => (
              <option key={value} value={value}>
                {S.issues.area[value]}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={S.issues.kindField}
            help={S.issues.kindHelp}
            value={kind}
            onChange={(value) => setKind(value as IssueKind)}
          >
            {ISSUE_KINDS.map((value) => (
              <option key={value} value={value}>
                {S.issues.kind[value]}
              </option>
            ))}
          </SelectField>
          <Field
            label={S.issues.stepField}
            help={S.issues.stepHelp}
            value={step}
            onChange={setStep}
          />
        </div>

        <TextAreaField
          label={S.issues.noteField}
          help={S.issues.noteHelp}
          rows={4}
          value={note}
          onChange={setNote}
        />

        <div className="btnrow">
          <Button tone="secondary" size="sm" onClick={() => input.current?.click()}>
            {S.issues.shotsAdd}
          </Button>
          {staged.length > 0 && <span className="cap">{S.issues.shotsStaged(staged.length)}</span>}
        </div>

        {problem !== null && (
          <p className="note" style={{ color: 'var(--danger)' }} role="alert">
            {problem}
          </p>
        )}

        {/* Visually hidden, but never nameless — a file input with no label is the one thing
            axe calls critical. */}
        <input
          ref={input}
          type="file"
          className="vh"
          multiple
          aria-label={S.issues.shotsChoose}
          accept={TYPES.join(',')}
          onChange={(event) => stage(event.target.files)}
        />
      </div>
    </Dialog>
  );
}
